import React from "react"
import * as O from "fp-ts/Option"
import { PriceResponseV2 } from "@ssc-booking/api-contracts"
import { Modal, ModalBody } from "@silversea-ssc/ui-components-react"
import useSanityContent from "../../app-core/_common/hooks/use-sanity-content"
import {
    SanitySuiteDeck,
    SanitySuiteModalQueryResponse,
    toSanityLocale,
} from "../../app-core/_common/sanity/sanity-types"
import { suiteContentQuery } from "../../app-core/_common/sanity/sanity-queries"
import PageSpinner from "../PageSpinner/PageSpinner"
import { Locale } from "../../app-core/_common/format/localization"
import { isPresent } from "../../app-core/_common/checks"
import { totalDecks } from "../../app-core/features/suite/helpers"
import { CompletedShipDeckPlanSlideProps } from "../LocalizedShipDeckPlansSlider/LocalizedShipDeckPlansSlider"
import { maybeSuiteWithConfigurationByCode } from "../../app-core/features/_common/read-models/suite-category"
import useModal from "../ModalProvider/useModal"
import SuiteModalContent from "./SuiteModalContent"
import { SuiteModalSelectButton } from "./SuiteModalSelectButton"
import { SuiteDetailsPrice } from "./SuiteDetailsPrice"

interface SuiteModalProps {
    locale: Locale
    shipVersionKey: string
    suitePrice: PriceResponseV2
    isSelected: boolean
    shipId: string
    SuiteModalSelectButton: SuiteModalSelectButton
    SuiteDetailsPrice: SuiteDetailsPrice
}

const deckPlanImagesForSuiteCategory = (
    deckPlans: SanitySuiteDeck[] | undefined,
    suiteCategoryId: string,
    title: string,
    color: string,
): CompletedShipDeckPlanSlideProps[] | undefined => {
    if (!deckPlans) return undefined
    return deckPlans
        .filter((d) => isPresent(d) && d.deckSuiteCategories.map((x) => x.suiteCategory._ref).includes(suiteCategoryId))
        .map((d) => ({
            svgURL: d.deckImageURL,
            color: color,
            title: title,
            deckNumber: d.deckNumber,
        }))
}

const SuiteModal: React.FC<React.PropsWithChildren<SuiteModalProps>> = ({
    locale,
    shipVersionKey,
    suitePrice,
    isSelected,
    shipId,
    SuiteModalSelectButton,
    SuiteDetailsPrice,
}) => {
    const { closeModal } = useModal()
    const sanityLang = toSanityLocale(locale)
    const { isLoading, data: allSuitesContent } = useSanityContent<SanitySuiteModalQueryResponse>(
        `suites-modal-content`,
        suiteContentQuery,
        { shipId: shipId, shipVersionKey: shipVersionKey },
        sanityLang,
    )
    const suiteCategory = suitePrice.suiteCategory
    const allDeckPlans = allSuitesContent?.version.decks
    const suiteContent = allSuitesContent
        ? O.toUndefined(maybeSuiteWithConfigurationByCode(allSuitesContent.version.suites, suiteCategory))
        : undefined

    if (!suiteContent) return null

    const totalShipDecks = totalDecks(allDeckPlans)
    const deckPlansImages = deckPlanImagesForSuiteCategory(
        allDeckPlans,
        suiteContent.category._ref,
        suiteContent.card.title,
        suiteContent.color,
    )

    return (
        <Modal
            title={suiteContent.card.title}
            modal={{ isOpen: true, onRequestClose: () => closeModal("closeWithCallback") }}
            size="big"
            dataContentName={`modal_${suiteCategory}`}
        >
            <ModalBody variant="wide">
                {isLoading ? (
                    <PageSpinner />
                ) : (
                    <SuiteModalContent
                        price={suitePrice.price}
                        title={suiteContent.card.title}
                        suite={suiteContent}
                        suiteCategory={suiteCategory}
                        isCurrentSuite={isSelected}
                        deckPlans={deckPlansImages}
                        totalShipDecks={totalShipDecks}
                        availability={suitePrice.availability}
                        SuiteModalSelectButton={SuiteModalSelectButton}
                        SuiteDetailsPrice={SuiteDetailsPrice}
                    />
                )}
            </ModalBody>
        </Modal>
    )
}

export default SuiteModal
